import { HISTORICAL_POINTS_PER_PAGE } from '@/config/query/allVaults'
import { DEFAULT_HISTORICAL_GRANULARITY } from '@/config/query/queryParams'
import type { VaultApyRange } from '@/types/apy'
import type { SdkNetwork } from '@/types/network'
import type { ChartPeriod } from '@/types/periods'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQueries } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { subDays } from '@vaultsfyi/common/utils'
import { toUnixTimestamp } from '@vaultsfyi/common/utils'

const historicalApyQueryKey = ['historicalApy'] as const

type HistoricalApyQueryParam = {
  vaultId: string
  network: SdkNetwork
}

interface Props {
  queryParams: HistoricalApyQueryParam[]
  apyRange: VaultApyRange
  period: ChartPeriod
}

type ApyData = {
  base: number
  reward: number
  total: number
}

export type HistoricalApyChartData = {
  timestamp: number
  [vaultKey: string]: ApyData | number
}

const historicalApyQueryOptions = ({
  param,
  apyRange,
  period,
}: Omit<Props, 'queryParams'> & { param: HistoricalApyQueryParam }) => {
  const defiSdk = getDefiSdk()
  const pastDate = Math.floor(toUnixTimestamp(subDays(Date.now(), period)))
  const currentDate = Math.floor(toUnixTimestamp(Date.now()))
  const { vaultId, network } = param
  return queryOptions({
    queryKey: [...historicalApyQueryKey, { vaultId, network, apyRange, period }] as const,
    queryFn: () =>
      defiSdk.getVaultHistoricalApy({
        path: { vaultId, network },
        query: {
          perPage: HISTORICAL_POINTS_PER_PAGE,
          apyInterval: apyRange,
          granularity: DEFAULT_HISTORICAL_GRANULARITY,
          fromTimestamp: pastDate,
          toTimestamp: currentDate,
        },
      }),
    retry: 3,
    staleTime: 30 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })
}

export const useHistoricalApyQueryOptions = ({ queryParams, apyRange, period }: Props) => {
  return useQueries({
    queries: queryParams.map((param) => historicalApyQueryOptions({ param, apyRange, period })),
    combine: (results) => {
      const dataRecord: Record<number, HistoricalApyChartData> = {}

      results.forEach((result, index) => {
        if (!result.data?.data) return

        const param = queryParams[index]
        const vaultKey = `${param.vaultId}-${param.network}`

        for (const dataPoint of result.data.data) {
          const timestamp = dataPoint.timestamp

          if (!dataRecord[timestamp]) {
            dataRecord[timestamp] = { timestamp }
          }

          dataRecord[timestamp][vaultKey] = {
            base: dataPoint.apy.base,
            reward: dataPoint.apy.reward ?? 0,
            total: dataPoint.apy.total,
          }
        }
      })

      const sortedData = Object.values(dataRecord).sort((a, b) => a.timestamp - b.timestamp)

      return {
        data: sortedData,
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      }
    },
  })
}
